import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

export default function DolbyAtmosDialog({ release, onClose, onPaid }) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (release) setBusy(false);
  }, [release]);

  if (!release) return null;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/artist/releases/${release.release_id}/dolby-checkout`, {
        app_url: typeof window !== "undefined" ? window.location.origin : "",
      });
      if (data.already_paid) {
        toast.success("Already paid — submitting for review…");
        onPaid && onPaid(release.release_id);
        onClose();
        return;
      }
      const purchaseUrl = data.purchase_url || "";
      if (!purchaseUrl) throw new Error("No checkout URL returned");
      window.location.href = purchaseUrl;
    } catch (err) {
      toast.error(formatApiError(err, "Could not start the Dolby Atmos checkout"));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-md" data-testid="dolby-atmos-dialog">
        <DialogHeader>
          <div className="h-12 w-12 rounded-2xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-3">
            <Sparkles size={22} className="text-blue-300" />
          </div>
          <DialogTitle className="font-display text-2xl">Complete the Dolby Atmos add-on</DialogTitle>
          <DialogDescription className="text-slate-400">
            <span className="text-white">{release.title || "This release"}</span> is set to deliver in Dolby Atmos.
            One-time <span className="text-white">£40</span> add-on covers Apple Music, Tidal HiFi Plus and Amazon Music Unlimited.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-xs text-slate-400 space-y-1.5 mt-2">
          <li>· Secure Stripe checkout · resumes automatically on return</li>
          <li>· Refundable if the release is rejected in review</li>
          <li>· You can turn Atmos off in the editor to submit for free</li>
        </ul>
        <div className="flex items-center justify-end gap-2 pt-4">
          <button
            type="button"
            data-testid="dolby-cancel"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-sm px-4 py-2"
          >
            Not now
          </button>
          <button
            type="button"
            data-testid="dolby-pay"
            onClick={pay}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-sm font-medium px-4 py-2 hover:bg-slate-200 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <>Pay £40 <ArrowRight size={13} /></>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
